// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportsDataFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Reports Data Fetcher.
 */
import fetch from 'share/fetch/Izenda.Fetch';
import LoadingManager from 'modules/Izenda.LoadingManager';
import CacheUtils from 'share/utils/Izenda.CacheUtils';
import { isNil, join } from 'lodash/fp';
const ROOT_URL = 'report/';

const fetchers = {
  /**
   * Get all categories
   * @param {String} saveType
   * @returns {promise}
   */
  loadAllCategories(type, tenantId) {
    tenantId = isNil(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}allcategories/${type}/${tenantId}`).get();
  },

  /**
   * Get categories for copy popup
   * @param {Number}    type      report type
   * @param {String}    tenantId  current tenant id
   * @param {Boolean}   isGlobal  flag global report
   * @returns {Promise}
   */
  loadCategoriesForCopyPopup(type, tenantId, isGlobal) {
    isNil(tenantId) && (tenantId = '');
    return fetch(`${ROOT_URL}category/${type}/${tenantId}?isGlobal=${!!isGlobal}`).get();
  },

  /**
   * Get all sub-categories
   * @param {String} categoryId
   * @returns {promise}
   */
  loadSubCategoriesForCopyPopup(categoryId) {
    return fetch(`${ROOT_URL}subcategory/${categoryId}`).get();
  },

  /**
   * Load all report Categories
   * @param  {Object} data [description]
   * @return {Promise}      [description]
   */
  loadReportCategories(data, includeHashCode) {
    includeHashCode = isNil(includeHashCode) ? false : includeHashCode;
    return fetch(`${ROOT_URL}list?includeHashCode=${includeHashCode}`).post(data);
  },

  /**
   * Load list report history
   * @param {String} tenantId
   * @param {Object} pageInfo
   * @param {typeString} reportId
   * @returns {Array} List of report history
   */
  loadReportHistory(tenantId, pageInfo, reportId) {
    pageInfo = pageInfo || {};

    const requestData = {
      reportId,
      tenantId,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'version', descending: true }]
    };

    return fetch(`${ROOT_URL}history`).post(requestData);
  },

  /**
   * Update Subscribed Instances
   * @param {Array} subscriptions
   * @returns {Promise}
   */
  updateSubscriptions(instances) {
    instances = instances || [];
    return fetch(`${ROOT_URL}subscriptions`).post(instances);
  },

  /**
   * Load report subscription
   * @param  {string} tenantId
   * @param  {object} pageInfo
   * @param  {string} reportId
   */
  loadSubscription(tenantId, pageInfo, reportId, isSubscriptionMode) {
    pageInfo = pageInfo || {};

    const requestData = {
      reportId,
      isSubscription: isSubscriptionMode,
      tenantId,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'name', descending: true }]
    };

    return fetch(`${ROOT_URL}loadSubscriptions`).post(requestData);
  },

  /**
   * Load schedule
   * @param  {string} reportId
   * @param  {Object} subscriptions
   * @param  {Object} pageInfo
   */
  loadSchedules(reportId, subscriptions, pageInfo) {
    pageInfo = pageInfo || {};

    const requestData = {
      reportId,
      subscriptions,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'name', descending: true }]
    };

    return fetch(`${ROOT_URL}loadSchedules`).post(requestData);
  },

  /**
   * validate category name
   * @param  {object} category
   * @returns {Promise}
   */
  validateCategoryName(category) {
    return fetch(`${ROOT_URL}category`).post(category);
  },

  /**
   * Get Report Id by it's name and categoryName
   * @param catName
   * @param reportName
   */
  getReportIdByNameAndCateName(catName, reportName, tenantId) {
    tenantId = tenantId ? tenantId : '';
    reportName = reportName ? `?reportName=${reportName}` : '';
    catName = catName ? `&categoryName=${catName}` : '';
    return fetch(`${ROOT_URL}reportId/${tenantId}${reportName}${catName}`).get();
  },

  /**
   * Check whether report exists or not
   */
  validateExistingReport(cateName, reportName, tenantId) {
    const data = {
      tenantId,
      reportName,
      categoryName: cateName
    };
    return fetch(`${ROOT_URL}validateExistingReport`).post(data);
  },

  /**
   * Check whether fields exist in report or not
   * @param reportName
   * @param cateName
   * @param field
   * @returns {*}
   */
  validateExistingField(cateName, reportName, field, tenantId) {
    const data = {
      tenantId,
      reportName,
      categoryName: cateName,
      columnFullReference: field
    };
    return fetch(`${ROOT_URL}validateExistingField`).post(data);
  },

  /**
   * Rename report
   * @param  {Object} data [description]
   * @return {Promise}      [description]
   */
  renameReport(data) {
    return fetch(`${ROOT_URL}rename`).post(data);
  },

  /**
   * DeleteReport description]
   * @param  {[type]} data [description]
   * @return {[type]}      [description]
   */
  deleteReport(data, version) {
    version = version || '';

    return fetch(`${ROOT_URL}delete/${version}`).post(data);
  },

  /**
   * Delete a ReportCategory by id
   * @param {string} reportCategoryId
   * @returns {promise}
   */
  deleteReportCategory(reportCategoryId) {
    return fetch(`${ROOT_URL}category/${reportCategoryId}`).delete();
  },

  /**
   * Copy new report
   * @param  {[type]} data [description]
   * @return {Promise}      [description]
   */
  copyNewReport(data) {
    return fetch(`${ROOT_URL}copy`).post(data);
  },

  /**
   * [moveReport this function is move report category]
   * @param  {[type]} data [description]
   * @return {[Promise]}
   */
  moveReport(data) {
    return fetch(`${ROOT_URL}move`).post(data);
  },
  /**
   * Validate subscription instance.
   * @param  {Object} instance
   * @returns {Promise}
   */
  validateSubscription(instance) {
    return fetch(`${ROOT_URL}subscription/validate`).post(instance);
  },

  /**
   * Get email templates
   * @returns {promise}
   */
  fetchEmailTemplates(templateType) {
    return fetch(`${ROOT_URL}emailTemplates/${templateType}`).get();
  },

  /**
   * Load report at specific version
   * @param {String} reportId
   * @param {String} version
   * @returns {Promise}
   */
  loadReportVersion(reportId, version, data) {
    return fetch(`${ROOT_URL}version/${reportId}/${version}`).post(data || {});
  },

  /**
   * Delete a report history
   * @param {String} reportId
   * @param {String} version
   * @returns {Promise}
   */
  deleteReportHistory(reportId, version) {
    version = version || 0;

    return fetch(`${ROOT_URL}history/${reportId}/${version}`).delete();
  },

  /**
   * Report information
   * @param {String} reportId
   * @returns {Promise}
   */
  reportInfo(reportId, version) {
    version = version || null;

    return fetch(`${ROOT_URL}info/${reportId}/${version}`).get();
  },

  /**
   * Report information
   * @param {String} reportId
   * @returns {Promise}
   */
  reportByProperty: CacheUtils.memoize(
    (reportId, reportProperty) => fetch(`${ROOT_URL}reportByProperty/${reportId}/${reportProperty}`).get(),
    {
      resolver() {
        return join('_', arguments);
      }
    }
  ),

  /**
   * load list of report
   * @method function
   * @param  {[type]} querry [description]
   * @return {[type]}        [description]
   */
  searchReport(query) {
    return fetch(`${ROOT_URL}advancedSearch`).post(query);
  },

  /**
   * Load all filter field data for list filter
   * @param {Object} data
   * @returns {Promise}
   */
  loadAllFilterFieldsData: CacheUtils.memoize(data => fetch(`${ROOT_URL}loadAllFilterFieldsData`).post(data), {
    resolver(data) {
      return JSON.stringify(data);
    }
  }),

  /**
   * Detect Schema Change
   * @param {type} data
   * @returns {type}
   */
  detectSchemaChange(data) {
    return fetch(`${ROOT_URL}detectSchemaChange`).post(data);
  },

  /**
   * Load accesses
   * @param  {string} reportId
   * @param  {Object} subscriptions
   * @param  {Object} pageInfo
   */
  loadAccesses(reportId, data, pageInfo) {
    pageInfo = pageInfo || {};

    const requestData = {
      reportId,
      accesses: data,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'all', descending: true }]
    };

    return fetch(`${ROOT_URL}loadAccesses`).post(requestData);
  },

  /**
   * update renderingTime
   * @param  {string} reportId
   * @param  {Number} renderingTime
   */
  updateRenderingTime(reportId, renderingTime) {
    const requestData = {
      id: reportId,
      renderingTime
    };

    return fetch(`${ROOT_URL}updateRenderingTime`).post(requestData);
  },

  /**
   * Clear Archived Version
   * @return {Promise} [description]
   */
  clearArchivedVersion() {
    return fetch(`${ROOT_URL}deleteAllArchiveVersions`).post();
  },

  postPrintDraftData(draftData) {
    return fetch(`${ROOT_URL}printDraft`).post(draftData);
  },

  getPrintDraftData(draftId) {
    return fetch(`${ROOT_URL}printDraft/${draftId}`).get();
  },

  validateRequiredFilters(reportId) {
    return fetch(`${ROOT_URL}validateFilter/${reportId}`).get();
  },

  /**
   * Load all report Categories
   * @param  {Object} data [description]
   * @return {Promise}      [description]
   */
  lazyLoadingReports(data, includeHashCode) {
    let url = 'list2';
    url += !isNil(includeHashCode) ? `?includeHashCode=${includeHashCode}` : '';
    return fetch(ROOT_URL + url).post(data);
  },

  /**
   * Get all categories
   * @param {Object} param
   * @returns {promise}
   */
  loadLazyLoadingCategories(param) {
    return fetch(`${ROOT_URL}allcategories/`).post(param);
  },

  /**
   * Search report Categories
   * @param  {Object} data [description]
   * @return {Promise} array of ReportCategories
   */
  lazyLoadingSearchCategories(request) {
    return fetch(`${ROOT_URL}list2`).post(request);
  },

  /**
   * Load categories data for lazy loading
   * @param {Object} request 
   */
  loadLazyLoadingAllowedSavingCategories(request) {
    return fetch(`${ROOT_URL}allowedSavingCategories`).post(request);
  },

  /**
   * Load subcategory for dashboard (lazyload)
   */
  loadLazyLoadAllowedSavingSubCategories(searchFilter) {
    return fetch(`${ROOT_URL}allowedSavingSubCategories`).post(searchFilter);
  }
};

LoadingManager.linkToLoadingProgress(fetchers, {
  updateSubscriptions: {
    allowToCancel: false
  },

  renameReport: {
    allowToCancel: false
  },

  deleteReport: {
    allowToCancel: false
  },

  deleteReportCategory: {
    allowToCancel: false
  },

  copyNewReport: {
    allowToCancel: false
  },

  moveReport: {
    allowToCancel: false
  },

  deleteReportHistory: {
    allowToCancel: false
  },

  lazyLoadingReports: {
    allowToCancel: false
  },

  loadLazyLoadingCategories: {
    allowToCancel: false
  },

  lazyLoadingSearchCategories: {
    allowToCancel: false
  }
});

export default fetchers;



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.ReportsDataFetcher.js